/**
 * A client screen opened by its address by someone its sidebar item is hidden from (N9) — or for a
 * client whose registration type the screen does not serve (FR-3).
 *
 * It rendered, called the service, and showed whatever each refused call said. It now asks the
 * question the sidebar asks, from the same table (`lib/screenAccess.ts`), and says one sentence.
 * The service's refusal stands behind this unchanged — this is how the screen says it, not what
 * protects the data.
 *
 * **FR-3 (ADR-0008 §Decision-3, D-150).** After the permission, a return or reconciliation screen
 * asks the client's `surfaces` — the service's gate table, served on the client record — and for a
 * record-only type renders "not yet supported" **instead of the screen**, so no request is made and
 * no 409 is ever shown. The sidebar keeps the item: ADR-0008 says the surface renders the state.
 * A client record with no `surfaces` at all is gated (fails closed, as the service's table does).
 * Periods opened while the client was regular are hidden by this, not deleted.
 */

import { Alert } from '@fiveox/ui'
import type { ReactNode } from 'react'
import { useOutletContext } from 'react-router'

import { useEvaluation } from '../lib/access'
import type { ClientRecord } from '../lib/api'
import { REGISTRATION_TYPES, registrationTypeLabel } from '../lib/portalLookup'
import {
  type ClientScreen,
  type OrgScreen,
  type Surface,
  needsOf,
  surfaceOf,
} from '../lib/screenAccess'
import type { ClientContext } from './ClientLayout'

/** What each gated surface's state says first — the service's own refusal, word for word. */
export const NOT_SUPPORTED: Record<Surface, string> = {
  returns: 'Return preparation is not yet supported for this registration type.',
  recon: 'Reconciliation is not yet supported for this registration type.',
}

export function ScreenGate({
  screen,
  children,
}: {
  screen: ClientScreen | OrgScreen
  children: ReactNode
}) {
  const { can, loading } = useEvaluation()
  // `undefined` on an org-level screen (the calendar), which no client is behind.
  const client = useOutletContext<ClientContext | undefined>()?.client
  if (loading) return null
  const [resource, verb] = needsOf(screen)
  if (!can(resource, verb)) {
    return (
      <div className="gst-empty">
        Your role in this firm does not include this screen. Ask whoever manages ProGST for your
        firm if you need it.
      </div>
    )
  }
  const surface = surfaceOf(screen)
  if (surface && client && client.surfaces?.[surface] !== true) {
    return <NotSupported client={client} surface={surface} />
  }
  return <>{children}</>
}

function NotSupported({ client, surface }: { client: ClientRecord; surface: Surface }) {
  const label = registrationTypeLabel(client.registration_type)
  const named = REGISTRATION_TYPES.some(([, words]) => words === label)
  return (
    <Alert tone="info">
      <p className="mb-1 font-medium">{NOT_SUPPORTED[surface]}</p>
      <p>
        {client.legal_name} is registered{' '}
        {named ? `as ${label}` : 'under a registration type ProGST has no name for'}. ProGST keeps
        its records — Upload Data, Raw Data and Activity work as usual — but does not prepare or
        reconcile its returns.
      </p>
    </Alert>
  )
}
